export const FLATPICKR_RANGE_SEPARATOR = '-';
export const FLATPICKR_LOCALE_FALLBACK = 'en';

const BROWSER_LOCALE_ALIASES = {
    vi: 'vn',
    'vi-vn': 'vn',
    vi_vn: 'vn',
};

export const parseFlatpickrFirstDayOfWeek = (value, fallback = 1) => {
    const firstDayOfWeek = parseInt(value, 10);
    if (Number.isNaN(firstDayOfWeek) || firstDayOfWeek < 0 || firstDayOfWeek > 6) {
        return fallback;
    }

    return firstDayOfWeek;
};

export const getFlatpickrFirstDayOfWeekFromObject = (source, fallback = 1) => {
    if (!source || typeof source !== 'object') {
        return fallback;
    }

    return parseFlatpickrFirstDayOfWeek(source.flatpickr_first_day_of_week, fallback);
};

export const buildLocaleCandidates = (locale) => {
    if (!locale || typeof locale !== 'string') {
        return [];
    }

    const localeTrimmed = locale.trim();
    if (!localeTrimmed) {
        return [];
    }

    const localeLower = localeTrimmed.toLowerCase();
    const localeUnderscore = localeLower.replace(/-/g, '_');
    const localeHyphen = localeLower.replace(/_/g, '-');
    const localeBase = localeLower.split(/[-_]/)[0];
    const localeVariants = [localeLower, localeUnderscore, localeHyphen, localeBase].filter(Boolean);
    const aliasedVariants = localeVariants
        .map((candidate) => BROWSER_LOCALE_ALIASES[candidate])
        .filter(Boolean);

    return [...new Set([...localeVariants, ...aliasedVariants])];
};

export const getBrowserLocaleCandidates = () => {
    const locales = [];

    if (typeof navigator !== 'undefined') {
        if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
            locales.push(...navigator.languages);
        }

        if (navigator.language) {
            locales.push(navigator.language);
        }
    }

    const localeCandidates = [];
    locales.forEach((locale) => {
        localeCandidates.push(...buildLocaleCandidates(locale));
    });

    localeCandidates.push(FLATPICKR_LOCALE_FALLBACK);

    return [...new Set(localeCandidates)];
};

export const getLocaleConfigFromCollection = (flatpickrLocales, localeCode) => {
    if (!flatpickrLocales || typeof flatpickrLocales !== 'object') {
        return null;
    }

    const keys = buildLocaleCandidates(localeCode);

    for (const key of keys) {
        if (flatpickrLocales[key] && typeof flatpickrLocales[key] === 'object') {
            return flatpickrLocales[key];
        }
    }

    return null;
};

export const resolveBrowserLocaleConfig = (flatpickrLocales) => {
    const browserLocaleCandidates = getBrowserLocaleCandidates();

    for (const localeCode of browserLocaleCandidates) {
        const localeConfig = getLocaleConfigFromCollection(flatpickrLocales, localeCode);
        if (localeConfig) {
            return {
                localeCode,
                localeConfig,
            };
        }
    }

    return {
        localeCode: 'default',
        localeConfig: null,
    };
};

export const splitDateRangeValue = (value, separator = FLATPICKR_RANGE_SEPARATOR) => {
    if (!value || typeof value !== 'string') {
        return [];
    }

    const escapedSeparator = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const rangeRegex = new RegExp(`\\s*${escapedSeparator}\\s*`);
    const parts = value.split(rangeRegex).map((part) => part.trim()).filter(Boolean);

    return parts.length === 2 ? parts : [];
};
